import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Box,
  Card,
  CardContent,
  Alert,
  Button,
  Container
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import MainLayout from '../../components/layouts/MainLayout';
import LoadingOverlay from '../../components/LoadingOverlay';
import BalanceHttpService from '../../api/BalanceHttpService';
import withAuthAdmin from '../../middleware/useWithAuthAdmin';
import BankAccountHttpService from "../../api/BankAccountHttpService";

function UserBalance() {
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [currentBalance, setCurrentBalance] = useState(0);
  const [bankAccount, setBankAccount] = useState(null);
  const [bankAccountLoading, setBankAccountLoading] = useState(false)

  const fetchCurrentBalance = async () => {
    try {
      const response = await BalanceHttpService.getUserBalanceLogs(1, 1);
      if (response.data && response.data.length > 0) {
        setCurrentBalance(response.data[0].new_balance);
      }
    } catch (error) {
      console.error('Error fetching current balance:', error);
    }
  };

  function userHasBankAccount() {
    if (bankAccountLoading) {
      return true;
    }

    return Object.entries(bankAccount ?? {}).length > 0;
  }

  const fetchBankAccount = async () => {
    setBankAccountLoading(true);
    const bankAccount = await BankAccountHttpService.getBankAccount();
    setBankAccount(bankAccount);
    setBankAccountLoading(false);
  }

  const fetchLogs = async (pageNumber = 1, limit = rowsPerPage) => {
    try {
      setLoading(true);
      const response = await BalanceHttpService.getUserBalanceLogs(pageNumber, limit);
      setLogs(response.data);
      setTotalItems(response.pagination.total);
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCurrentBalance();
    fetchBankAccount();
  }, []);

  useEffect(() => {
    fetchLogs(page + 1, rowsPerPage);
  }, [page, rowsPerPage]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const formatValue = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return <LoadingOverlay open={true} />;
  }

  return (
    <MainLayout>
      <Container>
        <Box sx={{mt:4, mb: 4, display: 'flex', alignItems: 'center' }}>
          <Typography variant="h4" component="h1">
            Meu Saldo
          </Typography>
        </Box>

        {
          !userHasBankAccount() && (
              <Box sx={{mt:4, mb: 4, display: 'flex', alignItems: 'center' }}>
                <Typography variant="span" component="span" color='red'>
                  Voce não tem conta bancaria cadastrada, faça o cadastro dos dados bancarios para possibilitar o resgate
                </Typography>
              </Box>
          )
        }

        <Card 
          elevation={3}
          sx={{
            mt: 1,
            mb: 4,
            background: '#FF6B00',
            color: 'white'
          }}
        >
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
              <AccountBalanceWalletIcon sx={{mt:5, fontSize: 40 }} />
              <Box>
                <Typography variant="h6" gutterBottom sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  Seu Saldo Atual
                </Typography>
                <Typography variant="h3" sx={{ fontWeight: 'bold' }}>
                  {formatValue(currentBalance)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell align="right">Saldo Anterior</TableCell>
                  <TableCell align="right">Valor Adicionado</TableCell>
                  <TableCell align="right">Novo Saldo</TableCell>
                  <TableCell>Tipo</TableCell>
                  <TableCell>Origem</TableCell>
                  <TableCell>Atualizado Por</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.log_id}>
                    <TableCell>{formatDate(log.date)}</TableCell>
                    <TableCell align="right">{formatValue(log.old_balance)}</TableCell>
                    <TableCell align="right">{formatValue(log.added_value)}</TableCell>
                    <TableCell align="right">{formatValue(log.new_balance)}</TableCell>
                    <TableCell>{log.type}</TableCell>
                    <TableCell>{log.origin ?? 'Vai pra conta'}</TableCell>
                    <TableCell>{log.updated_by}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[10, 20, 30]}
            component="div"
            count={totalItems}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Itens por página"
          />
        </Paper>
      </Container>
    </MainLayout>
  );
}

export default withAuthAdmin(UserBalance);
